import React, { ClipboardEvent, useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import axios from "axios";
import { APIResource } from "../../../../constants/application";
import { getUrl } from "utils/getURL";
import { useTranslation } from "react-i18next";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, Drawer, InputLabel, Stack, TextField, Typography } from "@mui/material";
import { FaAngleRight } from "@react-icons/all-files/fa/FaAngleRight";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@shared/loader";
import MobileDropdown from "@shared/mobileDropdown";
import SearchIFSC from "./searchIFSC";
import MessageViewer from "@shared/messageViewer";
import UserDetails from "@shared/userDetails";
import StickyActionContainer from "@shared/stickyActionContainer";
import {
  IValidateBankAPIData,
  setBankStatus,
  setError,
  ValidateBank,
} from "../../../../store/slices/validateBank";
import { IAllData } from "../KYCsteps";
import { ControlsContainer, RedAsterisk } from "../../../../styles/controls";
import {
  clearBankDetails,
  IBankBranchDetails,
  setBankDetails,
} from "../../../../store/slices/ifscSlice";
import { IBasicDetailsForm } from "../basicDetails";
import { ACCOUNT_OPTIONS } from "../../../../constants/accountTypeOptions";
import { MessageViewerContainer } from "./bankDetails.styles";
import BankDiffAcc from "./bankDiffAcc/bankDiffAcc";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import { KYCConstants } from "constants/application";
import FileUploaderWithCamera from "@shared/fileUploaderWithCamera";
import { FaCamera } from "@react-icons/all-files/fa/FaCamera";
import { FaFileUpload } from "@react-icons/all-files/fa/FaFileUpload";
import axiosInstance from "utils/axiosInstance";

export interface IBankDetailsForm {
  accType: string;
  bankAccountNumber: string;
  ifscCode: null | IBankBranchDetails;
  reEnterBankAccountNumber: string;
  cancelledCheque: any;
  accountProofDocumentUrl: string | undefined;
  nameMismatchReason?: string;
  gstCertificateDocumentUrl?: string;
  gstCertificatePublicUrl?: string;
  gstNumber?: string;
  gstFirmName?: string;
  isGstVerified?: boolean;
}

export const BankDetailsFormDefaultValues: IBankDetailsForm = {
  accType: "",
  bankAccountNumber: "",
  ifscCode: null,
  reEnterBankAccountNumber: "",
  cancelledCheque: null,
  accountProofDocumentUrl: '',
  nameMismatchReason: '',
  gstCertificateDocumentUrl: '',
  gstCertificatePublicUrl: '',
  gstNumber: '',
  gstFirmName: '',
  isGstVerified: false,
};

interface IBankDetails {
  nextStep: (data: IBankDetailsForm | IBasicDetailsForm) => void;
  formData: IAllData;
}

interface PresignedUrlResponse {
  expiresAt: string;
  objectKey: string;
  presignedUrl: string;
  publicUrl: string;
}

const getPresignedUrl = async ({
  customerId,
  fileName,
  fileType,
}: {
  customerId: string;
  fileName: string;
  fileType: string;
}) => {

console.log("CALL CAME")
  
  const nameParts = fileName.split("/");
  const lastPart = nameParts[nameParts.length - 1];
  const docTypeMatch = lastPart.match(/^(.*?)-/);
  const documentType = 'BANK_ACCOUNT_PROOF';
  const fileExtension = fileName.split(".").pop() || "";

  const response = await axiosInstance.post(
    // Using getUrl to construct the path with customerId, similar to validateBank
    getUrl(APIResource.uploadCancelledCheque, [
      {
        variableName: "customerId",
        variableValue: customerId,
      },
    ]),
    {
      documentType,
      fileExtension,
    },
    {
      params: { source: "SEMINIS" },
    }
  );

  return response.data as PresignedUrlResponse;
};

const fileToBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const uploadFileToS3 = async (uploadUrl: string, file: File) => {
  // Sending the file object directly is the standard and most reliable way to upload to a pre-signed S3 URL.
  // The Content-Type header must exactly match what was used to generate the URL on the backend.
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type,
      // S3 infers the content encoding from the request, so we don't need to set 'Content-Encoding' here
      // when sending a blob.
    },
  });
};

function BankDetails({ nextStep, formData }: IBankDetails) {
  console.log(formData,'-ppppppp')
  const { t } = useTranslation();
  const appName: any = localStorage.getItem("appname");
  const dispatch = useDispatch();
  const info = useSelector((state: any) => state.info.allData);
  const [isAccountMasked, setIsAccountMasked] = useState<boolean>(true);
  const [accountOption, setAccountOption] = useState(ACCOUNT_OPTIONS);
  const bankValidation = useSelector((state: any) => state.bank);
  const panValidation = useSelector((state: any) => state.pan);
  const methods = useForm<IBankDetailsForm>({
    mode: "all",
    defaultValues: BankDetailsFormDefaultValues,
  });

  const {
    control,
    getValues,
    watch,
    reset,
    trigger,
    formState: { isValid, isDirty, errors },
  } = methods;
  const bankAccountNumber = watch("bankAccountNumber");

  useEffect(() => {
    // When ever bank number changes, trigger validation on re-enter bank number field.
    if (isDirty || errors.reEnterBankAccountNumber)
      trigger("reEnterBankAccountNumber");
  }, [bankAccountNumber]);

  useEffect(() => {
    // Reset validation status when form data changes.
    if (isDirty) {
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }) as any);
    }
  }, [isDirty]);

  useEffect(() => {
    // Once bank is verified, reset the form so that any further change to form data will make form dirty.
    if (bankValidation.success) {
      const _formData = getValues();
      reset(_formData);
    }
  }, [bankValidation.success]);

  useEffect(() => {
    // Load form data from previous step
    let _formData: IBankDetailsForm | null = null;

    if (
      (formData.establishmentType === "Partnership" ||
        formData.establishmentType === "Company") &&
      formData.accType === "Savings"
    ) {
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
      // reset the bank form & reset the ifsc code
      _formData = {
        ...BankDetailsFormDefaultValues,
      };
      dispatch(clearBankDetails() as any);
    } else {
      _formData = {
        ...BankDetailsFormDefaultValues,
        bankAccountNumber: formData.bankAccountNumber,
        reEnterBankAccountNumber: formData.bankAccountNumber,
        ifscCode: formData.ifscCode,
        accType: formData.accType,
        cancelledCheque: formData.accountProofDocumentUrl || formData.cancelledCheque,
      };
    }
    // If establishment type is partnership or company, disable savings account option.
    if (
      formData.establishmentType === "Partnership" ||
      formData.establishmentType === "Company"
    ) {
      const _updatedOption = ACCOUNT_OPTIONS.map((item) =>
        item.value === "Savings" ? { ...item, isEnabled: false } : item
      );
      setAccountOption(_updatedOption);
    } else {
      setAccountOption([...ACCOUNT_OPTIONS]);
    }
    reset(_formData);
  }, []);

  const actionHandler = () => {
    // If bank is already verified, move to next step. else validate bank details.
    if (bankValidation.bankStatus === BANK_STATUS_VARIANTS.BANK_VERIFIED) {
      nextStep(getValues());
    } else {
      handleBankValidation();
    }
  };

  const handleBankValidation = async () => {
    const _formData = getValues();
    const isSeminis = appName.toLowerCase() === "seminis";

    dispatch(setError({ value: "" }));

    let accountProofDocumentUrl: string | undefined;
   
    if (isSeminis) {
      // Perform upload only if not Seminis
      if (!_formData.cancelledCheque) {
        // This should be caught by form validation, but as a safeguard.
        dispatch(setError({ value: "Cancelled cheque is required." }));
        return;
      }
      try {
        // 1. Get a presigned URL and upload the file first.
        const { cancelledCheque } = _formData;
        const fileName = `${cancelledCheque!.name}`;
        const customerId = info?.customerId;

        const presignedUrlResponse = await getPresignedUrl({
          customerId,
          fileName,
          fileType: cancelledCheque!.type,
        });

        if (!presignedUrlResponse.presignedUrl) {
          throw new Error("Could not get an upload URL.");
        }

        await uploadFileToS3(presignedUrlResponse.presignedUrl, cancelledCheque);
        accountProofDocumentUrl = presignedUrlResponse.objectKey;
      } catch (error: any) {
        console.error("File upload failed:", error);
        dispatch(setError({ value: "File upload failed. Please try again." }));
        return; // Stop if upload fails
      }
    }

    // 2. Validate bank details (always runs).
    const _apiData: IValidateBankAPIData = {
      customerId: info?.customerId,
      bankAccountNumber: _formData.bankAccountNumber,
      ifscCode: _formData.ifscCode?.ifscCode || "",
      accountType: _formData.accType,
      userProvidedName:
        panValidation?.panResponse?.fullName || info?.ownerName || info?.shopName || "1234",
      customerSource: "",
      accountProofDocumentUrl: accountProofDocumentUrl, // Will be undefined for Seminis, as intended
    };
    dispatch(ValidateBank({ data: _apiData }) as any);
  };

  // disable copy paste functionality accounts field
  const handleCopyChange = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const bankLinkageAction = (actionType: "CONTINUE" | "RESET") => {
    if (actionType === "CONTINUE") {
      dispatch(
        setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED }) as any
      );
    } else {
      dispatch(setBankDetails({ value: null }));
      reset(BankDetailsFormDefaultValues);
      dispatch(
        setBankStatus({
          value: BANK_STATUS_VARIANTS.PENDING,
        }) as any
      );
    }
  };

  return (
    <Stack direction={"column"} flexGrow={1}>
      <UserDetails />
      {bankValidation?.error && (
        <Alert severity="error" sx={{ ml: 2.25, mb: 2.25, mr: 2.25 }}>
          {bankValidation?.error}
        </Alert>
      )}
      <FormProvider {...methods}>
        <form autoComplete="off">
          <ControlsContainer>
            <MobileDropdown
              name={"accType"}
              label={t("acc_type", { defaultValue: "Account Type" })}
              placeholder={t("select_acc_type", {
                defaultValue: "Select Account Type",
              })}
              title={t("select_acc_type", {
                defaultValue: "Select Account Type",
              })}
              options={accountOption}
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
              }}
              note={
                formData.establishmentType === "Partnership" ||
                formData.establishmentType === "Company"
                  ? t("acc_selection_note", {
                      defaultValue:
                        "Partnership and Company firms are required to use a current account.",
                    })
                  : undefined
              }
            />

            <Controller
              control={control}
              name="bankAccountNumber"
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <InputLabel>
                    {t("account_number", {
                      defaultValue: "Account Number",
                    })}
                    <RedAsterisk>&thinsp;*</RedAsterisk>
                  </InputLabel>
                  <TextField
                    {...field}
                    type={isAccountMasked ? "password" : "tel"}
                    onFocus={() => setIsAccountMasked(false)}
                    onBlur={() => {
                      field.onBlur();
                      setIsAccountMasked(true);
                    }}
                    fullWidth={true}
                    onCopy={handleCopyChange}
                    onPaste={handleCopyChange}
                    error={error !== undefined}
                    required
                    slotProps={{
                      htmlInput: {
                        maxLength: KYCConstants.accountNumberCharLimit,
                      },
                      input: {
                        placeholder: t("enter_acc_number", {
                          defaultValue: "Enter Account Number",
                        }),
                      },
                    }}
                    helperText={
                      <>
                        {error && (
                          <MessageViewer
                            status="error"
                            details={error?.message}
                          />
                        )}
                      </>
                    }
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="reEnterBankAccountNumber"
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
                validate: (value) =>
                  bankAccountNumber === value ||
                  t("acc_match_error_text", {
                    defaultValue:
                      "Account numbers don’t match. Please check again.",
                  }),
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <InputLabel>
                    {t("re_acc_number", {
                      defaultValue: "Re-enter Account Number",
                    })}
                    <RedAsterisk>&thinsp;*</RedAsterisk>
                  </InputLabel>
                  <TextField
                    {...field}
                    type={"tel"}
                    onCopy={handleCopyChange}
                    onPaste={handleCopyChange}
                    fullWidth={true}
                    error={error !== undefined}
                    helperText={
                      <>
                        {error && (
                          <MessageViewer
                            status="error"
                            details={error?.message}
                          />
                        )}
                      </>
                    }
                    slotProps={{
                      htmlInput: {
                        maxLength: KYCConstants.accountNumberCharLimit,
                      },
                      input: {
                        placeholder: t("enter_acc_number", {
                          defaultValue: "Enter Account Number",
                        }),
                      },
                    }}
                  />
                </div>
              )}
            />
            <SearchIFSC
              name="ifscCode"
              label={t("ifsc_code", { defaultValue: "IFSC Code" })}
              rules={{
                required: t("required_text", {
                  defaultValue: "This is required field",
                }),
              }}
              placeholder={t("search_ifsc", {
                defaultValue: "Search by IFSC Code",
              })}
              title={t("search_ifsc", { defaultValue: "Search by IFSC Code" })}
            />
            {appName.toLowerCase() === "seminis" && (
              <Controller
                control={control}
                name="cancelledCheque"
                rules={{
                  required: t("required_text", {
                    defaultValue: "This is required field",
                  }),
                }}
                render={({ field, fieldState: { error } }) => (
                  <FileUploaderWithCamera
                    {...field}
                    label={t("upload_cheque", {
                      defaultValue: "Upload cancelled cheque or passbook photo",
                    })}
                    error={error?.message}
                  />
                )}
              />
            )}
            {bankValidation?.bankStatus ===
              BANK_STATUS_VARIANTS.BANK_VERIFIED && (
              <MessageViewerContainer>
                <MessageViewer
                  status={"success"}
                  details={t("bank_verified", {
                    defaultValue: "Bank details successfully verified.",
                  })}
                  extraDetails={
                    bankValidation?.bankResponse?.accountHolderName || ""
                  }
                />
              </MessageViewerContainer>
            )}
          </ControlsContainer>
        </form>
      </FormProvider>
      <StickyActionContainer>
        <Button
          onClick={actionHandler}
          size="large"
          fullWidth
          variant={
            bankValidation?.bankStatus === BANK_STATUS_VARIANTS.BANK_VERIFIED
              ? "contained"
              : "outlined"
          }
          disabled={!isValid || bankValidation.loading}
          endIcon={
            bankValidation?.bankStatus === BANK_STATUS_VARIANTS.BANK_VERIFIED ? (
              <FaAngleRight size={15} />
            ) : bankValidation.loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
        >
          {bankValidation?.bankStatus === BANK_STATUS_VARIANTS.BANK_VERIFIED
            ? t("next", { defaultValue: "Next" })
            : bankValidation.loading
            ? t("verifying", { defaultValue: "Verifying..." })
            : t("verify_bank_details", { defaultValue: "Verify Bank Details" })}
        </Button>
      </StickyActionContainer>
      {bankValidation.loading && <Loader />}
      {bankValidation?.bankStatus === "BANK_ASSOCIATION" && (
        <BankDiffAcc bankAction={bankLinkageAction} />
      )}
    </Stack>
  );
}

export default BankDetails;
